import { createContext, useCallback, useContext, useEffect, useRef } from 'react'
import { Outlet } from 'react-router'
import { v4 as uuidv4 } from 'uuid'

import { startTimeOnPageMeter } from '@/lib/timeOnPage'
import { postTimeOnPage } from '@/models/insuranceApplication'
import { useCustomerApplication } from './useCustomerApplication'

const REPORT_EVERY_MS = 30_000

const QuotesSeenContext = createContext<(() => void) | null>(null)

/**
 * Wraps the application chat and the quotes page, so one clock times the customer from their first
 * question to their first priced quote. The backend starts and stops it; staff boards and the shared
 * quotes link sit outside this layout and never report.
 */
export function TimeToQuotingLayout() {
  const { application } = useCustomerApplication()
  const markQuotesSeen = useTimeOnPageReports(application?.id)

  return (
    <QuotesSeenContext.Provider value={markQuotesSeen}>
      <Outlet />
    </QuotesSeenContext.Provider>
  )
}

/** Stops the clock once a priced quote is on the customer's screen. */
export function useQuotesSeen(seen: boolean) {
  const markQuotesSeen = useContext(QuotesSeenContext)
  useEffect(() => {
    if (seen) markQuotesSeen?.()
  }, [seen, markQuotesSeen])
}

function useTimeOnPageReports(applicationId: string | undefined): () => void {
  // Outside the effect: the page's own effects run first, so quotes can be on screen before the clock starts.
  const quotesOnScreen = useRef(false)
  const reportNow = useRef(() => {})

  useEffect(() => {
    if (!applicationId) return
    // Its own id, so the backend can tell this visit's running totals from another tab's.
    const pageViewId = uuidv4()
    const meter = startTimeOnPageMeter()
    // The newest total the backend took; repeating it would add nothing.
    let acknowledged = -1
    let stopped = false

    const report = () => {
      if (stopped) return
      // Quotes that arrived while the tab was hidden aren't seen until the customer comes back.
      const quotesSeen = quotesOnScreen.current && document.visibilityState === 'visible'
      // A price on screen ends it: the total freezes, so a retry can't add time spent reading quotes.
      if (quotesSeen) meter.stop()
      const seconds = meter.seconds()
      if (seconds === acknowledged && !quotesSeen) return
      postTimeOnPage(applicationId, { pageViewId, seconds, quotesSeen })
        .then((running) => {
          acknowledged = Math.max(acknowledged, seconds)
          if (!running) stop()
        })
        // A lost report costs nothing: the next one carries the same total and more.
        .catch(() => undefined)
    }
    const timer = setInterval(report, REPORT_EVERY_MS)
    const stop = () => {
      stopped = true
      meter.stop()
      clearInterval(timer)
      document.removeEventListener('visibilitychange', report)
    }

    // Hiding reports what was counted; coming back reports quotes that were waiting on screen.
    document.addEventListener('visibilitychange', report)
    reportNow.current = report
    // Right away, so the clock starts with the visit even if carriers are asked within seconds.
    report()

    return () => {
      reportNow.current = () => {}
      report()
      stop()
    }
  }, [applicationId])

  return useCallback(() => {
    if (quotesOnScreen.current) return
    quotesOnScreen.current = true
    reportNow.current()
  }, [])
}
